import React, { Component } from 'react';
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { reduxForm, Field, FieldArray, formValueSelector, change, reset } from 'redux-form'

// API
import * as API from '../../../../utils/api'

// Form Components
import ReduxFormInput from '../../../../components/Global/ReduxForms/ReduxFormInput'
import ReduxSelectInput from '../../../../components/Global/ReduxForms/ReduxSelectInput'
import ReduxDatePicker from '../../../../components/Global/ReduxForms/ReduxDatePicker'

// Helpers
import StatField from '../../../../utils/StatField'
import { required } from '../../../../utils/formValidators'

class GameForm extends Component {
  static propTypes = {
    onSubmit: PropTypes.func.isRequired,
    seasonID: PropTypes.number.isRequired,
    sportID: PropTypes.number.isRequired,
    gameID: PropTypes.number,
    game: PropTypes.object
  }

  state = {
    sport: null,
    gameID: null
  }

  componentDidMount() {
    this.loadSport(this.props.sportID)
  }

  componentWillReceiveProps(nextProps) {
    if (this.loading()) {
      this.loadSport(nextProps.sportID)
      return(null)
    }

    const { gameID } = this.state

    if (!gameID) {
      if (nextProps.gameID) {
        this.switchGames(nextProps.gameID, nextProps.game)
      } else if (this.props.currentPositionIds !== nextProps.currentPositionIds) {
        this.updatePlayerStatCategories(nextProps.currentPositionIds)
      }
    }

    if (gameID) {
      if (!nextProps.game) {
        this.switchGames(nextProps.gameID, nextProps.game)
      }

      if (nextProps.game) {
        if (gameID !== nextProps.gameID) {
          this.switchGames(nextProps.gameID, nextProps.game)
        }

        if (gameID === nextProps.gameID) {
          if (this.props.game !== nextProps.game) {
            this.switchGames(nextProps.gameID, nextProps.game)
          } else if (this.props.currentPositionIds !== nextProps.currentPositionIds && this.props.game.played_position_ids !== nextProps.currentPositionIds) {
            this.updatePlayerStatCategories(nextProps.currentPositionIds)
          }
        }
      }
    }
  }

  loading = () => !this.state.sport

  loadSport = (sportID) => {
    if (this.loading() || this.state.sport.id !== sportID) {
      API.getSport(sportID)
      .then(sport => this.setState({sport}))
    }
  }

  switchGames = (gameID, gameData) => {
    this.setState({ gameID }, () => {
      gameData ? this.setFormData(gameData) : this.resetForm()
    })
  }

  setFormData = (gameData) => {
    this.props.dispatch(change('GameForm', 'game', gameData))
  }

  resetForm = () => this.props.dispatch(reset('GameForm'))

  updatePlayerStatCategories = (positionIds) => {
    // Get the stat categories currently in the form
    const { currentStatCategories } = this.props

    // Get the positions whose stat categories need to be filled out
    const selectedPositions = this.selectedPositions(positionIds)

    // Get the required stat categories and their names based on the selected positions
    const ids = this.requiredStatCategoryIds(selectedPositions)
    const requiredCategories = this.requiredStatCategories(ids)
    const requiredNames = requiredCategories.map(c => c.name)

    // Remove categories that are no longer needed
    const filteredCategories = currentStatCategories.filter(category => (
      requiredNames.includes(category.name)
    ))

    // Find any missing required categories
    const missingCategories = this.missingCategories(filteredCategories, requiredCategories)

    // Convert missing categories to player stat categories
    const missingPlayerStatCategories = missingCategories.map(cat => this.asPlayerStats(cat))

    // Combine the current categories with the missing categories
    const updatedCategories = filteredCategories.concat(missingPlayerStatCategories)

    this.props.dispatch(change('GameForm', 'game.player_stats', updatedCategories))
  }

  selectedPositions = (positionIds) => {
    return this.state.sport.positions.filter(position => (
      positionIds.includes(position.id)
    ))
  }

  requiredStatCategoryIds = (positions) => {
    const allIds = positions.reduce((aggregator, position) => (
      aggregator.concat(position.required_stat_category_ids)
    ), [])

    return Array.from(new Set(allIds))
  }

  requiredStatCategories = (statCategoryIds) => (
    this.state.sport.stat_categories.filter(category => (
      statCategoryIds.includes(category.id)
    ))
  )

  missingCategories = (categoryList, requiredCategories) => {
    const listedNames = categoryList.map(c => c.name)
    return requiredCategories.filter(cat => !listedNames.includes(cat.name))
  }

  asPlayerStats = (statCategory) => ({
    name: statCategory.name,
    fields: statCategory.fields.map(field => ({
      ...field,
      value: ''
    }))
  })

  getHeadingText() {
    const { firstGame } = this.props
    const { gameID } = this.state

    if (firstGame) {
      return "Add Season's First Game"
    } else {
      return gameID ? "Edit Game" : "Add a New Game"
    }
  }

  render() {
    if (this.loading()) return null

    const { sport, gameID } = this.state
    const { firstGame } = this.props

    return (
      <form
        onSubmit={this.props.handleSubmit}
        autoComplete="off"
        className="game-form"
      >
        <h3>{this.getHeadingText()}</h3>
        <Field
          name="game[description]"
          type="text"
          label="Opponent / Event Name"
          placeholder="Greenon Knights"
          component={ReduxFormInput}
          validate={required}
        />
        <Field
          name="game[played_on]"
          type="text"
          label="Game Date"
          placeholder="01-01-2018"
          component={ReduxDatePicker}
          validate={required}
        />
        <Field
          name="game[location]"
          type="text"
          label="Location"
          component={ReduxFormInput}
          validate={required}
        />
        <FieldArray
          label="Positions Played"
          name="game[played_position_ids]"
          component={renderPlayedPositions}
          props={{positions: sport.positions}}
        />
        <FieldArray
          name="game[player_stats]"
          component={renderPlayerStats}
          props={{sport}}
        />
        <div className="game-form-actions">
          <button className="cta-gradient" type="submit">
            { gameID ? "Save Changes" : "Save Game"}
          </button>
          {
            (!gameID && !firstGame) &&
            <a
              className='text-link'
              onClick={this.props.handleCancelClick}
            >
              Cancel
            </a>
          }
        </div>
      </form>
    )
  }
}

const renderPlayedPositions = ({ fields, positions }) => (
  <ul className="played-positions-list">
    {
      fields.map((played_position, index) => (
        <li key={index} className="played-positions-list-item">
          <Field
            label="Position Played"
            name={played_position}
            component={ReduxSelectInput}
            validate={required}
            options={positions}
            valueKey="id"
            labelKey="name"
            parse={(value) => value.id}
            handleInputRemoval={fields.length > 1 ? () => fields.remove(index) : null}
          />
        </li>
      ))
    }
    <button type="button" className="cta invert" onClick={() => fields.push()}>
      + Add a Position
    </button>
  </ul>
)

const renderPlayerStats = ({ fields, sport }) => (
  <ul>
    {
      fields.map((category, index) => (
        <li key={index}>
          <h3>{fields.get(index).name}</h3>
          <FieldArray
            name={category + '.fields'}
            component={renderCategoryFields}
            props={{sport, statCategoryName: fields.get(index).name }}
          />
        </li>
      ))
    }
  </ul>
)

const renderCategoryFields = ({ fields, sport, statCategoryName }) => (
  <ul>
    {
      fields.map((field, index) => (
        <li key={index}>
          {
            StatField(sport.name, statCategoryName, fields.get(index).name, field, index, fields)
          }
        </li>
      ))
    }
  </ul>
)

const blankForm = {
  game: {
    description: '',
    location: '',
    played_on: '',
    played_position_ids: [null],
    player_stats: []
  }
}

// Export the component
GameForm = reduxForm({
  form: 'GameForm'
})(GameForm)

const mapStateToProps = (state) => {
  const selector = formValueSelector('GameForm')

  return ({
    currentPositionIds: selector(state, 'game.played_position_ids'),
    currentStatCategories: selector(state, 'game.player_stats'),
    initialValues: blankForm
  })
}

export default connect(mapStateToProps)(GameForm)



// WEBPACK FOOTER //
// ./src/components/UserPortal/Locker/Stats/GameForm.js